import { useAuth } from '@/app/AuthContext.jsx';
import usePreference from './usePreference';
import { expFeaturesPref } from '@/lib/expFeatures';

// ─────────────────────────────────────────────────────────────────────────────
// THE product-wide gate for experimental / «Próx» features.
//
// Anything marked «Próx» (in development, may change or fail) MUST be gated on
// this flag — not on a hardcoded const or a per-page boolean. It is the single
// source of truth the "Funciones experimentales" toggle (Configuración) drives.
//
// Semantics:
//   • INTERNAL-ONLY. Only internal (backoffice) users can enable it. For everyone else
//     the hook returns false regardless of the stored pref, and the settings toggle is
//     hidden. "Internal" is user-level (any internal membership), so it survives
//     switching profile.
//   • OFF by default for internal users (see expFeaturesPref). The Próx affordance
//     is hidden or disabled until the user opts in.
//   • ON  ⇒ the affordance appears / becomes usable, but KEEPS its «Próx» badge
//     (opting in does not promote it to a finished feature).
//
// How to gate a new Próx feature (nav section, bulk/multi-select, «en lote» /
// CSV button, any in-dev surface):
//
//     const [exp] = useExpFeatures();
//     // hide it:        {exp && <BulkBar />}
//     // or reveal-disabled: <button disabled={!exp}>… <Badge>Próx</Badge></button>
//
// Reactive: [exp, setExp]. Every subscriber re-renders when the flag flips —
// the Configuración toggle and the far-away consumers (Diligencias & Pagos
// bulk multi-select, the lote/CSV buttons) live in different React
// trees, so the bridge is a window CustomEvent (see createPreference), not
// shared React state.
//
// Current consumers (keep this list current when you add one):
//   • shared/Topbar         — notification bell
//   • shared/FeedbackWidget  — in-dev feedback widget
//   • pages/diligencias     — bulk select + "Pagar N" + selbar; "Crear en lote"
//   • pages/pagos           — Cola de pago checkboxes + paybar
//   • pages/causas          — "Importar CSV" + list multi-select
//                             (Exportar CSV / Asignar / Archivar)
//   • pages/facturacion     — Cuentas bancarias tab
//   • pages/configuracion   — the "Funciones experimentales" toggle itself
// ─────────────────────────────────────────────────────────────────────────────
export default function useExpFeatures() {
  // useAuth() is undefined in isolated / story renders (no auth provider); without the
  // ?? {} guard the destructure would throw.
  const { isInternal } = useAuth() ?? {};
  const [pref, setPref] = usePreference(expFeaturesPref);
  return [isInternal ? pref : false, setPref];
}
